import { TransformNullValue, ChartType } from '@diag-ui/chart'

export interface MetricsProps {
  category: string
  metrics_list: {
    title: string
    queries: {
      promql: string
      name: string
      type: ChartType
    }[]
    nullValue?: TransformNullValue
    unit: string
  }[]
}

export const TiDBMetrics: MetricsProps[] = [
  {
    category: 'Server',
    metrics_list: [
      {
        title: 'TiDB Uptime', // 图表标题
        queries: [
          {
            promql:
              '(time() - process_start_time_seconds{{label_cluster_id}, component="tidb"})', // promql 语句
            name: '{instance}', // legend name, 用于图例展示
            type: 'area' // 该语句要展示的图表类型
          }
        ],
        nullValue: TransformNullValue.AS_ZERO, // 当查询结果为空时，处理成 0 值，对标 grafana 上 [Panel] - [Stacking and null value] - [Null value] 的配置
        unit: 's' // 该图标的单位
      },
      {
        title: 'TiDB CPU Usage',
        queries: [
          {
            promql:
              'irate(process_cpu_seconds_total{{label_cluster_id}, component="tidb"}[2m])',
            name: '{instance}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'percentunit'
      },
      {
        title: 'TiDB Memory Usage',
        queries: [
          {
            promql:
              'process_resident_memory_bytes{{label_cluster_id}, component="tidb"}',
            name: 'process-{instance}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'bytes'
      }
    ]
  },
  {
    category: 'Performance',
    metrics_list: [
      {
        title: 'QPS',
        queries: [
          {
            promql:
              'sum(rate(tidb_executor_statement_total{{label_cluster_id}}[2m]))',
            name: 'Total',
            type: 'line'
          },
          {
            promql:
              'sum(rate(tidb_executor_statement_total{{label_cluster_id}}[2m])) by (type)',
            name: '{type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Connection count',
        queries: [
          {
            promql: 'sum(tidb_server_connections{{label_cluster_id}})',
            name: 'Total',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Failed Queries',
        queries: [
          {
            promql:
              'sum(rate(tidb_server_execute_error_total{{label_cluster_id}}[2m]))',
            name: '{type} @ {instance}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Transaction Per Second',
        queries: [
          {
            promql:
              'sum(rate(tidb_session_transaction_duration_seconds_count{{label_cluster_id}}[2m])) by (type, txn_mode)',
            name: '{type}-{txn_mode}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Query Duration',
        queries: [
          {
            promql:
              'sum(rate(tidb_server_handle_query_duration_seconds_sum{{label_cluster_id}, sql_type!="internal"}[2m])) / sum(rate(tidb_server_handle_query_duration_seconds_count{{label_cluster_id}, sql_type!="internal"}[2m]))',
            name: 'avg',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(tidb_server_handle_query_duration_seconds_bucket{{label_cluster_id}, sql_type!="internal"}[2m])) by (le))',
            name: '99',
            type: 'line'
          },
          {
            promql:
              'sum(rate(tidb_server_handle_query_duration_seconds_sum{{label_cluster_id}, sql_type!="internal"}[2m])) by (sql_type) / sum(rate(tidb_server_handle_query_duration_seconds_count{{label_cluster_id}, sql_type!="internal"}[2m])) by (sql_type)',
            name: 'avg-{sql_type}',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(tidb_server_handle_query_duration_seconds_bucket{{label_cluster_id}}[2m])) by (le,sql_type))',
            name: '99-{sql_type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      }
    ]
  }
]

export const TiKVMetrics: MetricsProps[] = [
  {
    category: 'Server',
    metrics_list: [
      {
        title: 'TiKV Uptime',
        queries: [
          {
            promql:
              '(time() - process_start_time_seconds{{label_cluster_id}, component=~".*tikv"})',
            name: '{instance}',
            type: 'line'
          }
        ],
        unit: 's'
      },
      {
        title: 'TiKV CPU Usage',
        queries: [
          {
            promql:
              'sum(rate(process_cpu_seconds_total{{label_cluster_id}, component=~".*tikv"}[2m])) by (instance)',
            name: '{instance}',
            type: 'line'
          }
        ],
        unit: 'percentunit'
      },
      {
        title: 'TiKV Memory Usage',
        queries: [
          {
            promql:
              'avg(process_resident_memory_bytes{{label_cluster_id}, component=~".*tikv"}) by (instance)',
            name: '{instance}',
            type: 'line'
          }
        ],
        unit: 'bytes'
      },
      {
        title: 'TiKV IO MBps',
        queries: [
          {
            promql:
              'sum(rate(tikv_engine_flow_bytes{{label_cluster_id},db="kv",type="wal_file_bytes"}[2m]))+(sum(rate(tikv_engine_flow_bytes{{label_cluster_id},db="raft",type="wal_file_bytes"}[2m]))or(0*sum(rate(raft_engine_write_size_sum{{label_cluster_id}}[2m]))))+(sum(rate(raft_engine_write_size_sum{{label_cluster_id}}[2m]))or(0*sum(rate(tikv_engine_flow_bytes{{label_cluster_id},db="raft",type="wal_file_bytes"}[2m]))))',
            name: '{instance}-write',
            type: 'line'
          },
          {
            promql:
              'sum(rate(tikv_engine_flow_bytes{{label_cluster_id},db="kv",type=~"bytes_read|iter_bytes_read"}[2m]))',
            name: '{instance}-read',
            type: 'line'
          }
        ],
        unit: 'Bps'
      },
      {
        title: 'TiKV Storage Usage',
        queries: [
          {
            promql:
              'sum(tikv_store_size_bytes{{label_cluster_id}, type="used"}) by (instance)',
            name: '{instance}',
            type: 'area'
          }
        ],
        unit: 'bytes'
      }
    ]
  },
  {
    category: 'Performance',
    metrics_list: [
      {
        title: 'QPS',
        queries: [
          {
            promql:
              'sum(rate(tikv_grpc_msg_duration_seconds_count{{label_cluster_id}, type!="kv_gc"}[2m])) by (instance,type)',
            name: '{instance}-{type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Connection count',
        queries: [
          {
            promql: 'sum(tidb_server_connections{{label_cluster_id}})',
            name: 'Total',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Failed Queries',
        queries: [
          {
            promql:
              'sum(rate(tidb_server_execute_error_total{{label_cluster_id}}[2m]))',
            name: '{type} @ {instance}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Transaction Per Second',
        queries: [
          {
            promql:
              'sum(rate(tidb_session_transaction_duration_seconds_count{{label_cluster_id}}[2m])) by (type, txn_mode)',
            name: '{type}-{txn_mode}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Query Duration',
        queries: [
          {
            promql:
              'sum(rate(tidb_server_handle_query_duration_seconds_sum{{label_cluster_id}, sql_type!="internal"}[2m])) / sum(rate(tidb_server_handle_query_duration_seconds_count{{label_cluster_id}, sql_type!="internal"}[2m]))',
            name: 'avg',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(tidb_server_handle_query_duration_seconds_bucket{{label_cluster_id}, sql_type!="internal"}[2m])) by (le))',
            name: '99',
            type: 'line'
          },
          {
            promql:
              'sum(rate(tidb_server_handle_query_duration_seconds_sum{{label_cluster_id}, sql_type!="internal"}[2m])) by (sql_type) / sum(rate(tidb_server_handle_query_duration_seconds_count{{label_cluster_id}, sql_type!="internal"}[2m])) by (sql_type)',
            name: 'avg-{sql_type}',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(tidb_server_handle_query_duration_seconds_bucket{{label_cluster_id}}[2m])) by (le,sql_type))',
            name: '99-{sql_type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      },
      {
        title: 'Avg TiDB KV Request Duration',
        queries: [
          {
            promql:
              'sum(rate(tidb_tikvclient_request_seconds_sum{{label_cluster_id}, store!="0"}[2m])) by (type)/ sum(rate(tidb_tikvclient_request_seconds_count{{label_cluster_id}, store!="0"}[2m])) by (type)',
            name: '{type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      },
      {
        title: 'Avg TiKV GRPC Duration',
        queries: [
          {
            promql:
              'sum(rate(tikv_grpc_msg_duration_seconds_sum{{label_cluster_id}, store!="0"}[2m])) by (type)/ sum(rate(tikv_grpc_msg_duration_seconds_count{{label_cluster_id}, store!="0"}[2m])) by (type)',
            name: '{type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      },
      {
        title: 'Average / P99 PD TSO Wait/RPC Duration',
        queries: [
          {
            promql:
              '(sum(rate(pd_client_cmd_handle_cmds_duration_seconds_sum{{label_cluster_id}, type="wait"}[2m])) / sum(rate(pd_client_cmd_handle_cmds_duration_seconds_count{{label_cluster_id}, type="wait"}[2m])))',
            name: 'wait - avg',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(pd_client_cmd_handle_cmds_duration_seconds_bucket{{label_cluster_id}, type="wait"}[2m])) by (le))',
            name: 'wait - 99',
            type: 'line'
          },
          {
            promql:
              '(sum(rate(pd_client_request_handle_requests_duration_seconds_sum{{label_cluster_id}, type="tso"}[2m])) / sum(rate(pd_client_request_handle_requests_duration_seconds_count{{label_cluster_id}, type="tso"}[2m])))',
            name: 'rpc - avg',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(pd_client_request_handle_requests_duration_seconds_bucket{{label_cluster_id}, type="tso"}[2m])) by (le))',
            name: 'rpc - 99',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      },
      {
        title: 'Average / P99 Storage Async Write Duration',
        queries: [
          {
            promql:
              'sum(rate(tikv_storage_engine_async_request_duration_seconds_sum{{label_cluster_id}, type="write"}[2m])) / sum(rate(tikv_storage_engine_async_request_duration_seconds_count{{label_cluster_id}, type="write"}[2m]))',
            name: 'avg',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(tikv_storage_engine_async_request_duration_seconds_bucket{{label_cluster_id}, type="write"}[2m])) by (le))',
            name: '99%',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      }
    ]
  }
]

export const PDMetrics: MetricsProps[] = [
  {
    category: 'Server',
    metrics_list: [
      {
        title: 'PD Uptime',
        queries: [
          {
            promql:
              '(time() - process_start_time_seconds{{label_cluster_id}, component=~".*pd.*"})',
            name: '{component}-{instance}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      },
      {
        title: 'PD CPU Usage',
        queries: [
          {
            promql:
              'irate(process_cpu_seconds_total{{label_cluster_id}, component=~".*pd.*"}[2m])',
            name: '{component}-{instance}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'percentunit'
      }
    ]
  },
  {
    category: 'Performance',
    metrics_list: [
      {
        title: 'QPS',
        queries: [
          {
            promql:
              'sum(rate(tidb_executor_statement_total{{label_cluster_id}}[2m]))',
            name: 'Total',
            type: 'line'
          },
          {
            promql:
              'sum(rate(tidb_executor_statement_total{{label_cluster_id}}[2m])) by (type)',
            name: '{type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Connection count',
        queries: [
          {
            promql: 'sum(tidb_server_connections{{label_cluster_id}})',
            name: 'Total',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Failed Queries',
        queries: [
          {
            promql:
              'sum(rate(tidb_server_execute_error_total{{label_cluster_id}}[2m]))',
            name: '{type} @ {instance}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Transaction Per Second',
        queries: [
          {
            promql:
              'sum(rate(tidb_session_transaction_duration_seconds_count{{label_cluster_id}}[2m])) by (type, txn_mode)',
            name: '{type}-{txn_mode}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 'short'
      },
      {
        title: 'Query Duration',
        queries: [
          {
            promql:
              'sum(rate(tidb_server_handle_query_duration_seconds_sum{{label_cluster_id}, sql_type!="internal"}[2m])) / sum(rate(tidb_server_handle_query_duration_seconds_count{{label_cluster_id}, sql_type!="internal"}[2m]))',
            name: 'avg',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(tidb_server_handle_query_duration_seconds_bucket{{label_cluster_id}, sql_type!="internal"}[2m])) by (le))',
            name: '99',
            type: 'line'
          },
          {
            promql:
              'sum(rate(tidb_server_handle_query_duration_seconds_sum{{label_cluster_id}, sql_type!="internal"}[2m])) by (sql_type) / sum(rate(tidb_server_handle_query_duration_seconds_count{{label_cluster_id}, sql_type!="internal"}[2m])) by (sql_type)',
            name: 'avg-{sql_type}',
            type: 'line'
          },
          {
            promql:
              'histogram_quantile(0.99, sum(rate(tidb_server_handle_query_duration_seconds_bucket{{label_cluster_id}}[2m])) by (le,sql_type))',
            name: '99-{sql_type}',
            type: 'line'
          }
        ],
        nullValue: TransformNullValue.AS_ZERO,
        unit: 's'
      }
    ]
  }
]
